import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { ActionDialog, ReasonModal } from '../../components/dialogs';
import { HistorySection, transferHistory } from '../../components/history';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  LoadError,
  metaCls,
  nameCls,
  numCls,
  Section,
  StatusBadge,
  StockTypeBadge,
} from '../../components/ui';
import { fmtDate, fmtDateTime, fmtQty, isExpired } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import { TRANSFER_STATUS } from '../../status';
import type { TransferDetail, TransferLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';

type BusyKind = 'approve' | 'prepare' | 'complete' | 'reject' | 'cancel';
/** How an action ended; `handled` = the caller showed the failure itself. */
type Outcome = 'done' | 'failed' | 'handled' | 'busy';

const LINE_COLUMNS: TableDataColumn<TransferLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Nama produk',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'batchNumber',
    title: 'Batch',
    render: (_v, l) => <span className={numCls}>{l.batchNumber}</span>,
  },
  {
    key: 'stockType',
    title: 'Tipe',
    render: (_v, l) => <StockTypeBadge type={l.stockType} />,
  },
  {
    key: 'expiredDate',
    title: 'Expiry',
    render: (_v, l) =>
      l.expiredDate ? (
        <span
          className={`${metaCls} ${isExpired(l.expiredDate) ? 'text-(--state-error-base)' : ''}`}
        >
          {fmtDate(l.expiredDate)}
          {isExpired(l.expiredDate) ? ' · expired' : ''}
        </span>
      ) : (
        <span className={metaCls}>Tanpa expiry</span>
      ),
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

/** A bin: its name path, the bare name without one. */
const binText = (name: string, path: string) => path || name;

const StorageTransferDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getTransfer(id, { signal }),
    [id, refreshToken]
  );
  const [busy, setBusy] = useState<BusyKind | null>(null);
  const busyRef = useRef(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.storageTransfer}
          backLabel="Transfer storage"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Transfer tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const t = detail.data;

  /**
   * One action at a time. The answer is the document's new state; a failure
   * is a toast (and a reload when the status moved under us).
   */
  const run = async (
    kind: BusyKind,
    call: () => Promise<TransferDetail>,
    success: string,
    onFail?: (err: unknown) => boolean
  ): Promise<Outcome> => {
    if (busyRef.current) return 'busy';
    busyRef.current = true;
    setBusy(kind);
    try {
      const next = await call();
      detail.setData(next);
      toast.success(success, { description: t.code });
      return 'done';
    } catch (err) {
      if (onFail?.(err)) return 'handled';
      const e = parseWarehouseError(err);
      toast.error(e.message);
      // Someone else acted first: show the document as it is now.
      if (e.status === 409) detail.reload();
      return 'failed';
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const approve = () =>
    run('approve', () => warehouseApi.approveTransfer(t.id), 'Transfer disetujui');
  const prepare = () =>
    run(
      'prepare',
      () => warehouseApi.prepareTransfer(t.id),
      'Barang sedang disiapkan'
    );
  const complete = async () => {
    const outcome = await run(
      'complete',
      () => warehouseApi.completeTransfer(t.id),
      'Transfer selesai, stok dipindahkan'
    );
    // Leave the dialog whatever the outcome: a failure is a toast on the page.
    if (outcome !== 'busy') setCompleting(false);
  };
  const reject = async (reason: string) => {
    setRejectError(null);
    const outcome = await run(
      'reject',
      () => warehouseApi.rejectTransfer(t.id, reason),
      'Transfer ditolak',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.status !== 422) return false;
        setRejectError(e.fieldErrors[0]?.message ?? e.message);
        return true;
      }
    );
    if (outcome === 'done' || outcome === 'failed') setRejecting(false);
  };

  /** APPROVED / PREPARING only: the stock was reserved but never moved. */
  const cancel = async (reason: string) => {
    setCancelError(null);
    const outcome = await run(
      'cancel',
      () => warehouseApi.cancelTransfer(t.id, reason),
      'Transfer dibatalkan',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.status !== 422) return false;
        setCancelError(e.fieldErrors[0]?.message ?? e.message);
        return true;
      }
    );
    if (outcome === 'done' || outcome === 'failed') setCancelling(false);
  };

  const sourceText = binText(t.sourceStorageName, t.sourceStoragePath);
  const destText = binText(t.destStorageName, t.destStoragePath);
  const idle = busy === null;
  const canCancel = t.status === 'APPROVED' || t.status === 'PREPARING';
  /** The reason is on the "Dibatalkan" timeline event (events: newest first). */
  const cancelEvent =
    t.status === 'CANCELLED'
      ? t.events.find((e) => e.status === 'CANCELLED')
      : undefined;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.storageTransfer}
        backLabel="Transfer storage"
        meta={
          <>
            Diajukan {fmtDateTime(t.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {t.createdBy}
            </span>
          </>
        }
        title={t.code}
        badges={<StatusBadge meta={TRANSFER_STATUS[t.status]} />}
        actions={
          <>
            {canCancel && (
              <Button
                tone="destructive"
                style="ghost"
                data-testid="transfer-cancel"
                disabled={!idle}
                onClick={() => {
                  setCancelError(null);
                  setCancelling(true);
                }}
                leftIcon={<i className="ri-forbid-line text-[14px]" />}
              >
                Batalkan
              </Button>
            )}
            {t.status === 'REQUESTED' && (
              <>
                <Button
                  tone="destructive"
                  style="ghost"
                  data-testid="transfer-reject"
                  disabled={!idle}
                  onClick={() => {
                    setRejectError(null);
                    setRejecting(true);
                  }}
                  leftIcon={<i className="ri-close-circle-line text-[14px]" />}
                >
                  Tolak
                </Button>
                <Button
                  data-testid="transfer-approve"
                  loading={busy === 'approve'}
                  disabled={!idle}
                  onClick={() => void approve()}
                  leftIcon={<i className="ri-check-line text-[14px]" />}
                >
                  Approve
                </Button>
              </>
            )}
            {t.status === 'APPROVED' && (
              <Button
                data-testid="transfer-prepare"
                loading={busy === 'prepare'}
                disabled={!idle}
                onClick={() => void prepare()}
                leftIcon={<i className="ri-box-3-line text-[14px]" />}
              >
                Siapkan barang
              </Button>
            )}
            {t.status === 'PREPARING' && (
              <Button
                data-testid="transfer-complete"
                disabled={!idle}
                onClick={() => setCompleting(true)}
                leftIcon={<i className="ri-flag-line text-[14px]" />}
              >
                Selesaikan
              </Button>
            )}
          </>
        }
      />

      {t.status === 'CANCELLED' && (
        <Alert
          status="information"
          appearance="stroke"
          size="lg"
          title="Transfer dibatalkan"
          data-testid="transfer-cancelled"
        >
          {cancelEvent?.note || '—'}
          {cancelEvent &&
            ` · ${fmtDateTime(cancelEvent.occurredAt)} · ${cancelEvent.actor}`}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section
            title="Daftar produk"
            description={`${t.lineCount} baris · ${fmtQty(t.totalQty)} total qty`}
          >
            <div data-testid="transfer-lines">
              <TableData size="sm" data={t.lines} columns={LINE_COLUMNS} />
            </div>
          </Section>
          <HistorySection entries={transferHistory(t.events)} />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Informasi">
            <KV label="Storage asal" value={sourceText} />
            <KV label="Storage tujuan" value={destText} />
            <KV label="Hub" value={t.hub?.name ?? `Hub #${t.hubID}`} />
            <KV label="Pemohon" value={t.createdBy} />
            <KV label="Catatan" value={t.notes} />
            {t.status === 'REJECTED' && (
              <KV label="Alasan penolakan" value={t.rejectReason} />
            )}
          </Section>
        </div>
      </div>

      <ReasonModal
        open={rejecting}
        title="Tolak transfer"
        subtitle={t.code}
        notice="Stok yang dicadangkan di storage asal dilepas kembali."
        confirmLabel="Tolak transfer"
        placeholder="Mis. stok dibutuhkan untuk order lain"
        busy={busy === 'reject'}
        error={rejectError}
        testId="transfer-reject-dialog"
        onConfirm={(reason) => void reject(reason)}
        onClose={() => setRejecting(false)}
      />
      <ReasonModal
        open={cancelling}
        title="Batalkan transfer"
        subtitle={t.code}
        notice="Stok yang dicadangkan di storage asal dilepas kembali. Transfer tidak bisa dilanjutkan."
        confirmLabel="Batalkan transfer"
        placeholder="Mis. salah memilih bin tujuan"
        busy={busy === 'cancel'}
        error={cancelError}
        testId="transfer-cancel-dialog"
        onConfirm={(reason) => void cancel(reason)}
        onClose={() => setCancelling(false)}
      />
      <ActionDialog
        open={completing}
        title="Selesaikan transfer"
        subtitle={t.code}
        description={`Stok dipindahkan sekarang dari ${sourceText} ke ${destText}. Tindakan ini tidak dapat dibatalkan.`}
        confirmLabel="Selesaikan transfer"
        busy={busy === 'complete'}
        testId="transfer-complete-dialog"
        onConfirm={() => void complete()}
        onClose={() => setCompleting(false)}
      />
    </div>
  );
};

export default StorageTransferDetailPage;
